/**
 * CareLogix™ ORACLE — AdminOne CEO Command Dashboard (Phase 42G).
 * Operator-only. Composes high-level signals from existing AdminOne surfaces.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const C = {
  bg:        "#0a0f0a",
  panel:     "rgba(13,26,15,0.85)",
  ivory:     "#f5f5dc",
  ivoryD:    "#d4d4aa",
  gold:      "#facc15",
  emerald:   "#7AC860",
  amber:     "#fcd34d",
  red:       "#ef4444",
  border:    "rgba(245,200,66,0.30)",
};

const S = {
  wrap:   { background: C.bg, minHeight: "100vh", color: C.ivory, padding: "26px 22px", fontFamily: "'Inter', system-ui, sans-serif" },
  kicker: { color: C.gold, fontSize: 11, letterSpacing: "0.22em", fontWeight: 800, textTransform: "uppercase" },
  title:  { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 28, margin: "6px 0 6px" },
  sub:    { color: C.ivoryD, fontSize: 14, lineHeight: 1.55, margin: "0 0 18px", maxWidth: 760 },
  grid:   { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 },
  card:   { background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 },
  ct:     { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: 15, margin: "0 0 10px" },
  kpi:    { fontFamily: "'Cinzel', serif", color: C.ivory, fontSize: 26, fontWeight: 700, lineHeight: 1 },
  row:    { display: "flex", justifyContent: "space-between", padding: "4px 0", color: C.ivoryD, fontSize: 12 },
  link:   { color: C.gold, fontSize: 12, fontWeight: 600, textDecoration: "none" },
  err:    { color: C.red, fontSize: 12 },
};

function adminToken() {
  try { return localStorage.getItem("carelogix_adminone_token") || ""; } catch { return ""; }
}

async function adminGet(path) {
  const tok = adminToken();
  const r = await fetch(`${API_BASE}${path}`, {
    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

export default function AdminOneCEODashboardPage() {
  const [logix2, setLogix2]       = useState(null);
  const [members, setMembers]     = useState(null);
  const [inboxSum, setInboxSum]   = useState(null);
  const [llmHealth, setLlmHealth] = useState(null);
  const [error, setError]         = useState(null);

  const load = useCallback(async () => {
    setError(null);
    const [a, b, c, d] = await Promise.allSettled([
      adminGet("/api/adminone/logix2/health"),
      adminGet("/api/adminone/membership/users"),
      adminGet("/api/adminone/inbox/summary"),
      adminGet("/api/oracle/llm/health"),
    ]);
    if (a.status === "fulfilled") setLogix2(a.value);
    if (b.status === "fulfilled") setMembers(b.value);
    if (c.status === "fulfilled") setInboxSum(c.value);
    if (d.status === "fulfilled") setLlmHealth(d.value);
    if ([a, b, c, d].every((r) => r.status === "rejected")) {
      setError("Unable to reach AdminOne services");
    }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const memberCount = Array.isArray(members) ? members.length : (members?.users?.length ?? "—");
  const newSignals  = inboxSum?.new_today ?? inboxSum?.unread ?? "—";
  const degraded    = logix2?.degraded_mode === true;

  return (
    <main style={S.wrap} data-testid="adminone-ceo-dashboard-page">
      <div style={S.kicker}>ADMINONE · CEO COMMAND</div>
      <h1 style={S.title}>CEO Command Dashboard</h1>
      <p style={S.sub}>
        Operator-only command surface for CareLogix ORACLE™. Composed from
        every AdminOne service — LOGIX-2 reliability, membership growth,
        intelligence inbox, system readiness, and global expansion.
      </p>
      {error && <p style={S.err} data-testid="ceo-dashboard-error">{error}</p>}

      <section style={S.grid}>
        <article style={S.card} data-testid="ceo-card-membership">
          <h2 style={S.ct}>Membership</h2>
          <div style={S.kpi} data-testid="ceo-membership-count">{memberCount}</div>
          <div style={{ ...S.row, marginTop: 6 }}><span>Active members</span><span>—</span></div>
          <Link to="/adminone/membership"      style={S.link}>Open membership console →</Link>
        </article>

        <article style={S.card} data-testid="ceo-card-logix2">
          <h2 style={S.ct}>LOGIX-2 Reliability</h2>
          <div style={S.kpi}>{logix2?.active_providers ?? "—"} / 2</div>
          <div style={{ ...S.row, marginTop: 6 }}>
            <span>Degraded mode</span>
            <span style={{ color: degraded ? C.red : C.emerald }}>{degraded ? "YES" : "NO"}</span>
          </div>
          <div style={S.row}><span>Limited-mode events (24h)</span><span>{logix2?.limited_mode_events_24h ?? "—"}</span></div>
          <Link to="/adminone/logix2-health" style={S.link}>Open LOGIX-2 health →</Link>
        </article>

        <article style={S.card} data-testid="ceo-card-llm">
          <h2 style={S.ct}>LLM Provider</h2>
          <div style={S.kpi}>{llmHealth?.provider_reachable ? "Healthy" : "Degraded"}</div>
          <div style={S.row}><span>Model</span><span>{llmHealth?.model || "—"}</span></div>
          <div style={S.row}><span>Configured</span><span>{llmHealth?.provider_configured ? "yes" : "no"}</span></div>
        </article>

        <article style={S.card} data-testid="ceo-card-inbox">
          <h2 style={S.ct}>Intelligence Inbox</h2>
          <div style={S.kpi}>{newSignals}</div>
          <div style={{ ...S.row, marginTop: 6 }}><span>New signals</span><span>—</span></div>
          <Link to="/adminone/inbox" style={S.link}>Open inbox →</Link>
        </article>

        <article style={S.card} data-testid="ceo-card-readiness">
          <h2 style={S.ct}>System Readiness</h2>
          <p style={{ color: C.ivoryD, fontSize: 12 }}>Provider status, env readiness, deployment posture.</p>
          <Link to="/adminone/system-readiness" style={S.link}>Open readiness →</Link>
        </article>

        <article style={S.card} data-testid="ceo-card-governance">
          <h2 style={S.ct}>Governance & Compliance</h2>
          <p style={{ color: C.ivoryD, fontSize: 12 }}>Policy manifest, audit trail, compliance posture.</p>
          <Link to="/adminone/policy" style={S.link}>Open governance →</Link>
        </article>

        <article style={S.card} data-testid="ceo-card-funding">
          <h2 style={S.ct}>Funding Intelligence</h2>
          <p style={{ color: C.ivoryD, fontSize: 12 }}>Grants, investors, strategic capital opportunities.</p>
          <Link to="/oracle/capital" style={S.link}>Open capital research →</Link>
        </article>

        <article style={S.card} data-testid="ceo-card-expansion">
          <h2 style={S.ct}>Global Expansion</h2>
          <p style={{ color: C.ivoryD, fontSize: 12 }}>Africa mesh, regional health/finance nodes.</p>
          <Link to="/adminone/africa-health" style={S.link}>Open expansion tracker →</Link>
        </article>
      </section>
    </main>
  );
}
